<script setup lang="ts">
withDefaults(defineProps<{
  label?: string
  tag?: string
  tagline?: string
  variant?: 'compact-dark' | 'compact-red' | 'compact-stack' | 'flame' | 'sticker'
  withTagline?: boolean
}>(), {
  label: 'Signal Fire Games',
  tag: 'Free games for youth groups',
  tagline: 'Free games for youth groups',
  variant: 'sticker',
  withTagline: false,
})
</script>

<template>
  <!-- Primary sticker lockup -->
  <SfgStickerBanner
    v-if="variant === 'sticker'"
    top-line="SIGNAL"
    bottom-line="FIRE GAMES"
    :tag
    fill="yellow"
    :tilt="1"
    size="md"
  />

  <!-- Hero lockup: full two-layer mark (no disk) + wordmark beside it -->
  <div v-else-if="variant === 'flame'" class="inline-flex items-center gap-4">
    <SfgFlame
      :size="96"
      :disk="false"
      body-color="red"
      flame-color="yellow"
      :tilt="2"
    />
    <div class="flex flex-col leading-none">
      <span class="font-display-alt text-[28px] text-brand-ink">SIGNAL</span>
      <span class="font-display text-[22px] text-brand-ink">FIRE GAMES</span>
    </div>
  </div>

  <!-- Compact on dark: flame mini + chalk wordmark (+ optional tagline at md+) -->
  <div v-else-if="variant === 'compact-dark'" class="inline-flex items-center gap-3">
    <SfgFlame
      size="clamp(36px, 1.8vw + 22px, 44px)"
      :disk="false"
      body-color="red"
      flame-color="yellow"
      :tilt="1"
    />
    <div class="flex flex-col leading-tight">
      <span class="font-display text-[14px] md:text-[16px] lg:text-[22px] xl:text-[26px] uppercase tracking-[0.08em] text-brand-chalk whitespace-nowrap">
        {{ label }}
      </span>
      <span
        v-if="withTagline && tagline"
        class="hidden md:block font-body text-[10px] lg:text-[12px] xl:text-[13px] text-brand-chalk/70 whitespace-nowrap tracking-wide"
      >
        {{ tagline }}
      </span>
    </div>
  </div>

  <!-- Compact on light: red wordmark (+ optional tagline at md+) -->
  <div v-else-if="variant === 'compact-red'" class="inline-flex items-center gap-3">
    <SfgFlame
      size="clamp(36px, 1.8vw + 22px, 44px)"
      :disk="false"
      body-color="red"
      flame-color="yellow"
      :tilt="1"
    />
    <div class="flex flex-col leading-tight">
      <span class="font-display text-[14px] md:text-[16px] lg:text-[22px] xl:text-[26px] uppercase tracking-[0.08em] text-brand-red whitespace-nowrap">
        {{ label }}
      </span>
      <span
        v-if="withTagline && tagline"
        class="hidden md:block font-body text-[10px] lg:text-[12px] xl:text-[13px] text-brand-ink/60 whitespace-nowrap tracking-wide"
      >
        {{ tagline }}
      </span>
    </div>
  </div>

  <!--
    Compact stacked: SIGNAL / FIRE / GAMES on three lines beside the
    flame. Used on XS viewports where the single-line wordmark + nav
    items would overflow; the three-line stack becomes an intentional
    secondary brand treatment rather than a cramped mistake.
  -->
  <div v-else-if="variant === 'compact-stack'" class="inline-flex items-center gap-3">
    <SfgFlame
      :size="44"
      :disk="false"
      body-color="red"
      flame-color="yellow"
      :tilt="1"
    />
    <div class="flex flex-col leading-[0.95]">
      <span class="font-display text-[13px] uppercase tracking-[0.08em] text-brand-red">SIGNAL</span>
      <span class="font-display text-[13px] uppercase tracking-[0.08em] text-brand-red">FIRE</span>
      <span class="font-display text-[13px] uppercase tracking-[0.08em] text-brand-red">GAMES</span>
    </div>
  </div>
</template>
